import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ShieldCheck } from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import { TOTAL_SERVICES } from "@/data/services";

const HERO_IMG =
  "https://images.pexels.com/photos/37730211/pexels-photo-37730211.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const RevealLine = ({ children, delay, className = "" }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const STATS = [
  { value: `${TOTAL_SERVICES}+`, label: "Infrastructure Services" },
  { value: "12", label: "Domain Verticals" },
  { value: "99.9%", label: "SLA Uptime Commitment" },
  { value: "Global", label: "Pan-India & 5 Intl. Hubs" },
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const imgY = useTransform(scrollYProgress, [0, 1], [0, -70]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section id="home" ref={ref} className="relative min-h-screen overflow-hidden flex items-center pt-28 pb-16">
      <motion.div style={{ y: bgY }} className="absolute inset-0 grid-bg" aria-hidden="true" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(56,189,248,0.09),transparent_55%)]" aria-hidden="true" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(99,102,241,0.07),transparent_50%)]" aria-hidden="true" />

      {[...Array(6)].map((_, i) => (
        <motion.span
          key={i}
          aria-hidden="true"
          className="absolute w-1.5 h-1.5 rounded-full bg-cyan-400/60"
          style={{ left: `${12 + i * 15}%`, top: `${20 + (i % 3) * 24}%` }}
          animate={{ y: [0, -18, 0], opacity: [0.25, 0.9, 0.25] }}
          transition={{ duration: 5 + i, repeat: Infinity, ease: "easeInOut", delay: i * 0.7 }}
        />
      ))}

      <motion.div style={{ opacity: fade }} className="relative max-w-7xl mx-auto px-6 w-full grid lg:grid-cols-12 gap-14 items-center">
        <div className="lg:col-span-7">
          <motion.div
            data-testid="hero-badge"
            initial={{ opacity: 0, x: -24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="inline-flex items-center gap-3 mb-8 px-4 py-2 rounded-full border border-cyan-400/25 bg-cyan-400/5"
          >
            <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse-dot" />
            <span className="font-mono text-[11px] tracking-[0.3em] text-cyan-300 uppercase">
              01 // Enterprise IT Infrastructure Provider
            </span>
          </motion.div>

          <h1
            data-testid="hero-headline"
            className="font-display font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-white"
          >
            <RevealLine delay={0.3}>Engineering Resilient</RevealLine>
            <RevealLine delay={0.45} className="metallic-text text-glow-cyan">
              Digital Infrastructure &amp;
            </RevealLine>
            <RevealLine delay={0.6}>Next-Gen Cyber Defense</RevealLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.85 }}
            className="mt-7 max-w-xl text-base text-slate-400 leading-relaxed"
          >
            Pan-India operations with remote support across the US, UK, UAE, Australia and
            Canada — delivering {TOTAL_SERVICES}+ tailored IT services across 12 core verticals.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1 }}
            className="mt-10 flex flex-wrap items-center gap-5"
          >
            <button
              data-testid="hero-explore-services-btn"
              onClick={() => scrollToId("services")}
              className="group flex items-center gap-3 px-7 py-4 rounded-full font-display font-semibold text-ark-950 bg-gradient-to-r from-slate-100 via-slate-300 to-slate-400 hover:shadow-[0_0_35px_rgba(56,189,248,0.4)] hover:scale-[1.03] transition-all duration-300"
            >
              Explore the Manifesto
              <ArrowDown size={16} className="group-hover:translate-y-0.5 transition-transform" />
            </button>
            <button
              data-testid="hero-book-audit-btn"
              onClick={() => scrollToId("contact")}
              className="flex items-center gap-3 px-7 py-4 rounded-full font-display font-semibold text-cyan-300 border border-cyan-400/40 hover:bg-cyan-400/10 hover:border-cyan-400/70 transition-all duration-300"
            >
              <ShieldCheck size={16} />
              Book IT Audit
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.15 }}
            className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-px bg-white/10 rounded-2xl overflow-hidden border border-white/10"
          >
            {STATS.map((s) => (
              <div key={s.label} className="bg-ark-900/90 px-5 py-6">
                <div className="font-display text-2xl font-bold text-white">{s.value}</div>
                <div className="mt-1 text-[11px] font-mono tracking-wider text-slate-500 uppercase">
                  {s.label}
                </div>
              </div>
            ))}
          </motion.div>
        </div>

        <motion.div style={{ y: imgY }} className="lg:col-span-5 relative hidden lg:block">
          <motion.div
            initial={{ opacity: 0, scale: 0.92, clipPath: "inset(100% 0 0 0)" }}
            animate={{ opacity: 1, scale: 1, clipPath: "inset(0% 0 0 0)" }}
            transition={{ duration: 1.3, delay: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="relative rounded-2xl overflow-hidden border border-white/15 shadow-[0_40px_80px_-20px_rgba(0,0,0,0.7)]"
          >
            <img
              src={HERO_IMG}
              alt="Enterprise server rack infrastructure"
              className="w-full h-[520px] object-cover saturate-[0.7] contrast-110"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ark-950/85 via-transparent to-ark-900/30" />
            <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between">
              <div>
                <p className="font-mono text-[10px] tracking-[0.3em] text-cyan-300 uppercase">Live Infrastructure</p>
                <p className="font-display text-lg font-semibold text-white mt-1">Deployed &amp; Monitored 24/7</p>
              </div>
              <img src="/assets/logo.png" alt="" className="h-14 w-14 rounded-full ring-1 ring-white/30" />
            </div>
          </motion.div>
          <motion.div
            aria-hidden="true"
            animate={{ y: [0, -14, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            className="absolute -top-8 -right-8 w-28 h-28 rounded-full border border-cyan-400/25"
          />
        </motion.div>
      </motion.div>
    </section>
  );
};
